import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import {
  type BoardTarget,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';

interface PassOnStudentModalProps {
  /** The student to pass on. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the pass-on has landed, so the board can refresh behind it. */
  onPassedOn?: () => void;
}

/**
 * The two-step pass-on popup.
 *
 * Unlike adopt, the confirm step posts no target ids: the server releases all
 * of the group's claims on the student regardless, so the list here is
 * read-only.
 */
export function PassOnStudentModal({
  studentId,
  onClose,
  onPassedOn,
}: PassOnStudentModalProps) {
  const { t } = useTranslation();
  const [passedOnName, setPassedOnName] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing now, readable from a settled request;
  // see `AdoptStudentModal`.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight; `isPending` disables the button a render late.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body. Adjusting state during render (rather than in an effect) keeps the
  // first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setPassedOnName(null);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  // Not `useStudentAdoptionInfo`, which is the adopt reading of this endpoint.
  // `mode=pass_on` checks only whether the student is already taken, so a lead
  // this helper can't adopt still opens its confirm body. `retry: false` and
  // the `enabled` switch are there for the hook's reasons.
  const {
    data: info,
    isLoading,
    error,
  } = api.getStudentAdoptionInfo.useQuery(
    { path: { student_id: studentId ?? 0 }, query: { mode: 'pass_on' } },
    { enabled: passedOnName === null && studentId !== null, retry: false },
  );
  const { mutateAsync: passOnStudent, isPending } =
    api.passOnStudent.useMutation();

  const failure = submitError ?? error;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  if (passedOnName !== null) {
    return (
      <StudentBoardModal
        title={t('wbs.student-has-been-passed-on')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.the-student-s-has-been-passed-on', { name: passedOnName })}
        </ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.pass-this-student-to-the-others')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.pass-this-student-to-the-others')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  const handlePassOn = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      const result = await passOnStudent({ body: { user_id: studentId } });
      // The server names the student it released, which is what the success
      // body prints; the confirm payload is stale by then.
      if (openSession.current === submittedFor) {
        setPassedOnName(result.display_name);
      }
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    onPassedOn?.();
  };

  return (
    <StudentBoardModal
      title={t('wbs.pass-this-student-to-the-others')}
      onClose={onClose}
      footer={
        <WBSButton
          onClick={() => {
            void handlePassOn();
          }}
          isLoading={isPending}
        >
          {t('wbs.pass-on-2')}
        </WBSButton>
      }
    >
      {/* The only place the user is told what pass-on does. */}
      <ModalParagraph>{t('wbs.pass-on-explainer')}</ModalParagraph>
      <StudentInfoBlock
        info={info}
        // Available, not original, targets: pass-on releases the still-claimed
        // ones, and legacy's list drops those already passed on.
        targets={info.available_targets}
        renderTargets={(targets: readonly BoardTarget[]) => (
          <ul>
            {targets.map((target) => (
              <li key={target.target_id}>{targetLabel(target)}</li>
            ))}
          </ul>
        )}
      />
    </StudentBoardModal>
  );
}
